<script setup>
import { DateTime } from "luxon";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { getAclStatus } from "../shared/services/aclStatus";
import {
  GRANT_STATUS_GREEN_CLS,
  GRANT_STATUS_ORANGE_CLS,
  GRANT_STATUS_WARNING_CLS,
  agenticGrantStatusChipClass,
  agenticGrantStatusKind,
  agenticGrantStatusLabelKey,
  agenticGrantStatusUsable,
  mostDisruptiveCode,
  ownerGrantStatusBadgeClass,
} from "../shared/services/agenticGrantStatus";
import { formatCountdown } from "../utils/formatCountdown";
import Tooltip from "./ui/Tooltip.vue";

const EXPIRING_COLOUR_CHANGE_TIME = 15 * 60; // Seconds

const { t } = useI18n();

const props = defineProps({
  status: {
    type: Number,
    required: true,
  },
  error: {
    type: String,
    default: null,
  },
  expiredAfter: {
    type: String,
    required: false,
  },
  clickToGrant: {
    type: Boolean,
    required: true,
  },
  delegatedIps: {
    type: Array,
    required: false,
    default: () => [],
  },
  grantTypeFixedIps: {
    type: Boolean,
    required: false,
    default: false,
  },
  currentAddress: {
    type: String,
    required: false,
    default: "",
  },
});

const statusConfig = computed(() => getAclStatus(props.status));
const isError = computed(() => statusConfig.value?.error ?? false);

const expiresAfter = ref(DateTime.fromISO(props.expiredAfter, { zone: "utc" }));
const expiresDiff = computed(() => (expiresAfter.value ? expiresAfter.value.diffNow("seconds").seconds : null));
const expiresRelative = computed(() => (expiresAfter.value ? expiresAfter.value.toRelative() : null));
const expiresSoon = computed(
  () => !isError.value && Boolean(expiresAfter.value) && expiresDiff.value < EXPIRING_COLOUR_CHANGE_TIME,
);

// Every device shows, even one sharing the browser's current IP: an agent or
// companion on the user's own machine is a real, separately-managed device
// with its own grant lifecycle, distinct from the knoc's own status row.
const persistentDevices = computed(() => props.delegatedIps ?? []);

const hasPersistentIPs = computed(() => persistentDevices.value.length > 0);
// Shared collapse semantics: the same rules the agentic popup uses, so the
// two surfaces always agree on what a status code means.
const isGrantedKind = (code) => agenticGrantStatusKind(code) === "active";

// The owner's own slot joins the aggregate only when they have (or are
// getting) their own access; on a click-to-grant/revoked owner the badge
// reflects the devices alone.
const ownerParticipates = computed(() => (statusConfig.value?.grantIssued || statusConfig.value?.pending) ?? false);

const participantCodes = computed(() => {
  const codes = persistentDevices.value.map((c) => c.Status);
  if (ownerParticipates.value) codes.unshift(props.status);
  return codes;
});

const grantedCount = computed(() => participantCodes.value.filter(isGrantedKind).length);
const usableCount = computed(() => participantCodes.value.filter((c) => agenticGrantStatusUsable(c)).length);
const totalIPs = computed(() => participantCodes.value.length);

const showAggregate = computed(
  () => hasPersistentIPs.value && (ownerParticipates.value || (statusConfig.value?.grantReady ?? false)),
);

// One aggregate badge across owner + companion + agentic devices:
// everything pending → "Pending N of N" (green when it is all published and
// just awaits the device's next fetch, orange while still applying); anything
// not pending → "Granted n of N" (green when complete, orange otherwise);
// nothing usable at all → the count plus the most disruptive failure
// ("Granted 0 of 2: Blocked"), coloured like the popup chips, so multiple
// failed devices aren't hidden behind one word.
const aggregate = computed(() => {
  if (!showAggregate.value) return null;
  const isPendingKind = (c) => agenticGrantStatusUsable(c) && !isGrantedKind(c);
  if (participantCodes.value.every(isPendingKind)) {
    const allPublished = participantCodes.value.every((c) => agenticGrantStatusKind(c) === "published");
    return {
      text: t("statuses.pendingCount", { pending: totalIPs.value, total: totalIPs.value }),
      cls: allPublished ? GRANT_STATUS_GREEN_CLS : GRANT_STATUS_WARNING_CLS,
    };
  }
  if (grantedCount.value > 0 || usableCount.value > 0) {
    return {
      text: t("statuses.grantedCount", { granted: grantedCount.value, total: totalIPs.value }),
      cls: grantedCount.value >= totalIPs.value ? GRANT_STATUS_GREEN_CLS : GRANT_STATUS_ORANGE_CLS,
    };
  }
  const worst = mostDisruptiveCode(participantCodes.value);
  return {
    text: t("statuses.grantedCountWithReason", {
      granted: 0,
      total: totalIPs.value,
      reason: t(`statuses.${worst}`),
    }),
    cls: agenticGrantStatusChipClass(agenticGrantStatusKind(worst)),
  };
});

const hasActiveExpiry = computed(() => !isError.value && Boolean(expiresAfter.value) && expiresDiff.value > 0);

// A confirmed grant whose allotment has run out but which the server still
// reports as granted (the cleanup + agent revoke hasn't completed yet). Avoids
// showing a bare "Granted" badge on an expired countdown during that window.
const hasExpired = computed(
  () =>
    !isError.value &&
    Boolean(expiresAfter.value) &&
    expiresDiff.value !== null &&
    expiresDiff.value <= 0 &&
    isGrantedKind(props.status),
);

const expiryDuration = computed(() => expiresRelative.value?.replaceAll("in ", ""));

const ownerLabel = computed(() =>
  props.grantTypeFixedIps ? t("statuses.predefinedIp") : props.currentAddress || t("statuses.yourIp"),
);

const agenticDevices = computed(() => persistentDevices.value.filter((c) => c.Source === "agentic_access"));
const companionDevices = computed(() => persistentDevices.value.filter((c) => c.Source !== "agentic_access"));

// Live per-device countdowns, agentic rows only: companion/link grants carry
// the session-end expiry, and a countdown to logout on every row is noise.
// Suppressed once the device is no longer usable (revoking/error/blocked).
const now = ref(Date.now());
const deviceCountdown = (c) => {
  if (c.Source !== "agentic_access" || !c.ExpiresAt || !agenticGrantStatusUsable(c.Status)) return null;
  return formatCountdown(c.ExpiresAt, now.value, t("agenticAccessPanel.expired"));
};

// The aggregate badge stays counts-only (each device can expire at a different
// time), but a clock marks that time-limited access is in the mix.
const hasTimeLimitedDevices = computed(() =>
  agenticDevices.value.some((c) => c.ExpiresAt && agenticGrantStatusUsable(c.Status)),
);

// "Created" is a placeholder (no grant yet), not an access state. The owner
// row only appears in the tooltip once they actually have/are getting access.
const showOwnerRow = computed(() => props.status !== 4);

// Kind labels collapse internal lifecycle names (e.g. a device sitting in
// Created is simply in flight → "Pending").
const deviceChipLabel = (code) => t(agenticGrantStatusLabelKey(agenticGrantStatusKind(code)));

// Status chip inside the tooltip rows: same collapse + colours as the badge
// and the agentic popup.
const deviceChipClass = (code) => agenticGrantStatusChipClass(agenticGrantStatusKind(code));

// Plain-text mirror of the structured tooltip panel, for screen readers.
const tooltipText = computed(() => {
  if (!hasPersistentIPs.value) return props.error ?? "";

  const lines = [];
  if (showOwnerRow.value) {
    lines.push(`${ownerLabel.value} (${t("statuses.device.browser")}): ${t(`statuses.${props.status}`)}`);
  }

  for (const c of persistentDevices.value) {
    const origin = c.Source === "agentic_access" ? t("statuses.device.agentic") : t("statuses.device.companion");
    const name = c.Name ? `, ${c.Name}` : "";
    const countdown = deviceCountdown(c);
    const expiry = countdown ? `, ${t("statuses.expiresIn", { duration: countdown })}` : "";
    lines.push(`${c.IP}${name} (${origin}): ${deviceChipLabel(c.Status)}${expiry}`);
  }

  if (props.error) lines.push(props.error);

  return lines.join("\n");
});

const clickToGrantLabel = computed(() => (props.clickToGrant ? t("statuses.access") : t(`statuses.${props.status}`)));

const badgeText = computed(() => {
  if (aggregate.value) {
    // No single duration: each IP can expire at a different time, so the count is not suffixed with a countdown.
    return aggregate.value.text;
  }
  if (hasExpired.value) {
    return t("statuses.expiring");
  }
  if (hasActiveExpiry.value) {
    return `${clickToGrantLabel.value} ${t("statuses.forDuration", { duration: expiryDuration.value })}`;
  }
  return t(`statuses.${props.status}`);
});

const badgeClass = computed(() => {
  if (aggregate.value) return aggregate.value.cls;
  return twMerge(ownerGrantStatusBadgeClass(props.status), expiresSoon.value && GRANT_STATUS_ORANGE_CLS);
});

let interval;

onMounted(() => {
  // One tick drives both the owner expiry and the per-device countdowns.
  interval = setInterval(() => {
    expiresAfter.value = DateTime.fromISO(props.expiredAfter, { zone: "utc" });
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  clearInterval(interval);
});
</script>

<template>
  <div>
    <Tooltip :focusable="true" aria-label="ACL status" :text="tooltipText">
      <template v-if="hasPersistentIPs" #content>
        <div class="min-w-44 space-y-1">
          <template v-if="showOwnerRow">
            <p
              class="flex items-center gap-1 pt-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground"
            >
              <font-awesome-icon :icon="['fass', 'browser']" class="h-3 w-3 shrink-0" aria-hidden="true" />
              {{ t("statuses.device.browser") }}
            </p>
            <div class="flex items-center justify-between gap-3 py-0.5">
              <span class="truncate font-mono text-[0.65rem]">{{ ownerLabel }}</span>
              <span
                class="inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-[0.65rem]"
                :class="deviceChipClass(status)"
                >{{ t(`statuses.${status}`) }}</span
              >
            </div>
          </template>
          <template v-if="agenticDevices.length > 0">
            <!-- Same icon as the Agentic tab in the access menu. -->
            <p
              class="flex items-center gap-1 pt-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground"
            >
              <font-awesome-icon :icon="['fass', 'circle-nodes']" class="h-3 w-3 shrink-0" aria-hidden="true" />
              {{ t("statuses.device.agentic") }}
            </p>
            <div v-for="c in agenticDevices" :key="c.IP" class="flex items-center justify-between gap-3 py-0.5">
              <span class="flex min-w-0 items-baseline gap-1.5">
                <!-- Chip-sized + break-all so a full IPv6 wraps instead of blowing out the panel. -->
                <span class="break-all font-mono text-[0.65rem]">{{ c.IP }}</span>
                <span v-if="c.Name" class="truncate text-muted-foreground">{{ c.Name }}</span>
              </span>
              <span class="flex shrink-0 items-center gap-1">
                <span v-if="deviceCountdown(c)" class="text-[0.65rem] tabular-nums text-green-500">{{
                  deviceCountdown(c)
                }}</span>
                <span
                  class="inline-flex items-center rounded-full border px-1.5 py-px text-[0.65rem]"
                  :class="deviceChipClass(c.Status)"
                  >{{ deviceChipLabel(c.Status) }}</span
                >
              </span>
            </div>
          </template>
          <template v-if="companionDevices.length > 0">
            <!-- Same icon as the Companion tab in the access menu. -->
            <p
              class="flex items-center gap-1 pt-0.5 text-[0.65rem] font-medium uppercase tracking-wide text-muted-foreground"
            >
              <font-awesome-icon :icon="['fass', 'laptop-mobile']" class="h-3 w-3 shrink-0" aria-hidden="true" />
              {{ t("statuses.device.companion") }}
            </p>
            <div v-for="c in companionDevices" :key="c.IP" class="flex items-center justify-between gap-3 py-0.5">
              <span class="flex min-w-0 items-baseline gap-1.5">
                <span class="break-all font-mono text-[0.65rem]">{{ c.IP }}</span>
                <span v-if="c.Name" class="truncate text-muted-foreground">{{ c.Name }}</span>
              </span>
              <span
                class="inline-flex shrink-0 items-center rounded-full border px-1.5 py-px text-[0.65rem]"
                :class="deviceChipClass(c.Status)"
                >{{ deviceChipLabel(c.Status) }}</span
              >
            </div>
          </template>
          <p v-if="error" class="pt-0.5 text-danger">{{ error }}</p>
        </div>
      </template>
      <div
        :class="
          twMerge(
            'inline-flex select-none items-center gap-1 text-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            badgeClass,
          )
        "
      >
        <!-- Same icon as the access menu trigger: marks that delegated
             (agentic/companion) devices are part of this status. -->
        <font-awesome-icon
          v-if="hasPersistentIPs"
          :icon="['fass', 'circle-nodes']"
          class="h-3 w-3 shrink-0"
          aria-hidden="true"
        />
        {{ badgeText }}
        <!-- Counts stay duration-free (devices expire at different times);
             the clock flags that time-limited access is in the mix. -->
        <font-awesome-icon
          v-if="aggregate && hasTimeLimitedDevices"
          :icon="['fass', 'clock']"
          class="h-3 w-3 shrink-0"
          :aria-label="t('statuses.timeLimited')"
          role="img"
        />
      </div>
    </Tooltip>
  </div>
</template>
